import { differenceInMilliseconds } from "date-fns";
import { decorate, observable } from "mobx";
import * as semver from "semver";
import { v4 as uuid } from "uuid";

import { clone } from "@formant/core/common/clone";
import { defined } from "@formant/core/common/defined";
import { delay } from "@formant/core/common/delay";
import { fork } from "@formant/core/common/fork";
import { logger } from "@formant/core/common/logger";
import {
    IPromiseIntervalHandle,
    setPromiseInterval,
} from "@formant/core/common/setPromiseInterval";
import { stats } from "@formant/core/common/stats";
import { throttle } from "@formant/core/common/throttle";
import { duration } from "@formant/core/model/duration";
import { IDevice } from "@formant/core/model/IDevice";
import { IDeviceConfiguration } from "@formant/core/model/IDeviceConfiguration";
import { IDeviceDetails } from "@formant/core/model/IDeviceDetails";
import { IOrganization } from "@formant/core/model/IOrganization";
import { IRtcPeer } from "@formant/core/model/IRtcPeer";
import { RtcConnectionStatus } from "@formant/core/model/RtcConnectionStatus";
import {
    rtcH264VideoFrameStreamType,
    rtcImageStreamTypes,
} from "@formant/core/model/rtcStreamTypes";
import { Timeout } from "@formant/core/model/Timeout";
import { Timestamp } from "@formant/core/model/Timestamp";
import { Uuid } from "@formant/core/model/Uuid";
import { IIceMode } from "@formant/rtc-client/models/IIceMode";
import { IPingInfo } from "@formant/rtc-client/models/IPingInfo";
import { IRtcConnectionStatsInfo } from "@formant/rtc-client/models/IRtcConnectionStatsInfo";
import { IRtcSendConfiguration } from "@formant/rtc-client/models/IRtcSendConfiguration";
import { IRtcSessionMetricsMessageCounts } from "@formant/rtc-client/models/IRtcSessionMetricsMessageCounts";
import { IRtcStreamMessage } from "@formant/rtc-client/models/IRtcStreamMessage";
import {
    IStreamControl,
    rtcPipelineType,
} from "@formant/rtc-client/models/IStreamControl";
import { RtcClient } from "@formant/rtc-client/RtcClient";
import { RtcClientV1 } from "@formant/rtc-client/RtcClientV1";
import { decodeStreamId } from "@formant/rtc-client/utils/encodeStreamId";
import { getRtcCapabilitySet } from "@formant/rtc-client/utils/getRtcCapabilitySet";
import { iceModeFromRtcConnectionStatsInfo } from "@formant/rtc-client/utils/iceModeFromRtcConnectionInfo";
import { peerToRtcPeer } from "@formant/rtc-client/utils/peerToRtcPeer";

import { track } from "../analytics";
import {
    deviceClient,
    rtcSignalingClient,
    signalingV2Client,
} from "../clients";
import { IAppStore } from "../store/IAppStore";
import { IAuthStore } from "../store/IAuthStore";
import { IDeviceConfigurationStore } from "../store/IDeviceConfigurationStore";
import { IDeviceDetailsStore } from "../store/IDeviceDetailsStore";
import { IOrganizationStore } from "../store/IOrganizationStore";
import { IRouterStore } from "../store/IRouterStore";
import { IRtcSignalingStore } from "../store/IRtcSignalingStore";
import { ISignalingStore } from "../store/ISignalingStore";
import { ITeleopSession, SessionLifecycle } from "./ITeleopSession";

const defaultSendConfig: IRtcSendConfiguration = {
    channelLabel: "stream.reliable",
};

const signalingV2Version = "1.19.3";

const unresponsiveRestartThreshold = 10 * duration.second;

const disconnectIntentTimeoutDuration = 3 * duration.second;

const rtcClientReadyTimeout = 10 * duration.second;

const disableTeleopDebugLog = true;

const teleopDebug = (message: string, meta?: object) => {
    if (!disableTeleopDebugLog) {
        logger.debug(message, meta);
    }
};

export class TeleopSession implements ITeleopSession {
    public rtcClient?: RtcClient | RtcClientV1;
    public agentVersion?: string;
    public sessionId: Uuid = "";
    public sessionStatus: SessionLifecycle = "noSession";
    public connectionStatus: RtcConnectionStatus = "disconnected";
    public ping?: number;
    public devicePeer: false | undefined | IRtcPeer = false; // False means no information yet. Undefined means does not exist currently.
    public ownerUserPeer: IRtcPeer | null | undefined;
    public owner: boolean = false;
    public iceMode?: IIceMode;
    public deviceConfiguration?: IDeviceConfiguration;
    public originalDeviceConfiguration?: IDeviceConfiguration;
    public updatedDeviceConfiguration?: IDeviceConfiguration;
    public startedTimestamp?: Timestamp = undefined;
    public connectedTimestamp?: Timestamp = undefined;
    public hasStarted: boolean = false;
    public hasStopped: boolean = false;
    public uuid = uuid();
    public onDisableControls?: () => void;
    public degradeVideoQuality = throttle((streamName: string) => {
        this.controlRemoteStream({
            streamName,
            degradeVideoQuality: true,
        });
    }, duration.second * 10);

    private organization?: IOrganization;
    private useSignalingV2 = false;
    private candidateTypesLog: string = "";
    // Maps stream ids to the number of local messages received on that stream,
    private recentPings: number[] = [];
    // and remote messages sent on that stream
    private localReceived = new Map<string, number>();
    private remoteSent = new Map<string, number>();
    private lastMessageTimestamp: Timestamp | undefined;
    private lastStreamsInfoUpdate: Timestamp | undefined;
    private deviceDetails?: IDeviceDetails;
    private updateInterval: IPromiseIntervalHandle | undefined;
    private connectTries: number = 0;
    private hasConnected: boolean = false;
    private registeredReceivers: {
        [streamName: string]: {
            [id: string]: (msg: IRtcStreamMessage) => void;
        };
    } = {};
    private reportFramesDroppedMap: {
        [id: string]: (...args: number[]) => void;
    } = {};
    private disconnectIntentTimeout?: Timeout;
    private disconnectIntent: boolean = false;
    private teleopRestartTimestamp?: Timestamp;
    private teleopInitialRestartTimestamp?: Timestamp;
    private disconnectTimestamp?: Timestamp;
    private disconnectAggregate = 0;
    private firstConnect = true;
    private commandStreamIdSentMap = new Map<
        string,
        { timestamp: Timestamp; count: number }
    >();
    private receivedStreams: { [id: string]: boolean } = {};

    private readonly appStore: IAppStore;
    private readonly authStore: IAuthStore;
    private readonly deviceConfigurationStore: IDeviceConfigurationStore;
    private readonly deviceDetailsStore: IDeviceDetailsStore;
    private readonly organizationStore: IOrganizationStore;
    private readonly routerStore: IRouterStore;
    private readonly rtcSignalingStore: IRtcSignalingStore;
    private readonly signalingStore: ISignalingStore;

    public static continueSession(session: ITeleopSession) {
        if (session.hasStopped) {
            return;
        }
        fork(session.start());
    }

    constructor(
        public deviceId: Uuid,
        private device: IDevice,
        stores: {
            appStore: IAppStore;
            authStore: IAuthStore;
            deviceConfigurationStore: IDeviceConfigurationStore;
            deviceDetailsStore: IDeviceDetailsStore;
            organizationStore: IOrganizationStore;
            routerStore: IRouterStore;
            rtcSignalingStore: IRtcSignalingStore;
            signalingStore: ISignalingStore;
        },
    ) {
        this.appStore = stores.appStore;
        this.authStore = stores.authStore;
        this.deviceConfigurationStore = stores.deviceConfigurationStore;
        this.deviceDetailsStore = stores.deviceDetailsStore;
        this.organizationStore = stores.organizationStore;
        this.routerStore = stores.routerStore;
        this.rtcSignalingStore = stores.rtcSignalingStore;
        this.signalingStore = stores.signalingStore;
    }

    public setLowBandwidthMode = (enable: boolean) => {
        this.controlRemoteStream({
            streamName: "all",
            setLowBandwidthMode: enable,
        });
    };

    public async start() {
        stats.increment("teleop_session_start", { deviceId: this.deviceId });
        const agentVersion = this.device?.state?.agentVersion;
        if (!agentVersion) {
            logger.warn(
                "Could not start teleop session. Undefined agent version.",
            );
            return;
        }

        this.startedTimestamp = new Date().getTime();
        this.hasStarted = true;
        this.agentVersion = agentVersion;
        this.useSignalingV2 = semver.lt(signalingV2Version, agentVersion);

        // Force refresh info
        await this.refreshSessionInfo();
        this.rtcClient = this.useSignalingV2
            ? new RtcClient({
                  signalingClient: signalingV2Client,
                  getToken: async () => await this.authStore.token(),
                  receive: (_: Uuid, message: IRtcStreamMessage) => {
                      this.receive(message);
                  },
                  onStreamsInfoUpdate: (_: Uuid, timestamp: Timestamp) => {
                      this.onStreamsInfoUpdate(timestamp);
                  },
                  onConnectionError: (error: unknown) => {
                      const message =
                          error instanceof Error
                              ? error.message
                              : String(error);
                      if (message.toLowerCase().includes("device locked")) {
                          this.appStore.setSnackbar(
                              "This device is currently claimed by someone else.",
                          );
                      }
                  },
                  track,
                  lockDevice: this.appStore.teleopLockDevice,
              })
            : new RtcClientV1({
                  signalingClient: rtcSignalingClient,
                  getToken: async () => await this.authStore.token(),
                  receive: (_: Uuid, message: IRtcStreamMessage) => {
                      this.receive(message);
                  },
              });

        if (this.isRtcV2(this.rtcClient)) {
            const waitStart = new Date().getTime();
            while (!this.rtcClient.isReady()) {
                await delay(50 * duration.millisecond);
                if (new Date().getTime() - waitStart > rtcClientReadyTimeout) {
                    throw new Error(
                        "Exceeded RTC Client ready timeout while loading teleop",
                    );
                }
            }
        }

        this.updateInterval = setPromiseInterval({
            func: async () => {
                await this.update();
            },
            delay: 1 * duration.second,
        });

        if (this.ownerUserPeer === undefined) {
            teleopDebug(
                "TeleopSession start: Owner user peer is undefined. Join session immediately...",
            );
            await this.join();
        }
    }

    public signalingVersion(): number | undefined {
        if (this.agentVersion) {
            const useSignalingV2 = semver.lt(
                signalingV2Version,
                this.agentVersion,
            );
            if (useSignalingV2) {
                return 2;
            } else {
                return 1;
            }
        }
        return undefined;
    }

    public isRtcV2(_: RtcClientV1 | RtcClient): _ is RtcClient {
        return this.signalingVersion() === 2;
    }

    public async restart() {
        this.onRestartSessionMetrics();

        const { devicePeer, rtcClient } = this;
        if (rtcClient && devicePeer) {
            teleopDebug("Disconnecting from device peer in restart call", {
                peerId: devicePeer.id,
                deviceId: devicePeer.deviceId,
            });
            await rtcClient.disconnect(devicePeer.id);
        }
        this.devicePeer = false;
        this.connectionStatus = "disconnected";
        this.sessionStatus = "noSession";
        this.connectTries = 0;
        this.hasConnected = false;
        this.connectedTimestamp = undefined;
        this.sessionId = "";
        this.ping = undefined;
        this.candidateTypesLog = "";

        // Refresh info
        await this.refreshSessionInfo();

        const { ownerUserPeer } = this;

        // Do not attempt to reconnect if the peer is offline, or we have incomplete information.
        if (!rtcClient || !devicePeer) {
            teleopDebug("TeleopSession restart: No device peer or RTC client.");
            return;
        }

        // Re-connect if there is no owner, or the owner is the local peer.
        if (ownerUserPeer !== null) {
            if (
                ownerUserPeer === undefined ||
                ownerUserPeer.id === (await rtcClient.getLocalPeer()).id
            ) {
                await this.join();
            }
        }
    }

    public setDisconnectIntent() {
        if (this.disconnectIntentTimeout) {
            clearTimeout(this.disconnectIntentTimeout);
        }
        this.disconnectIntent = true;

        this.disconnectIntentTimeout = setTimeout(() => {
            this.disconnectIntent = false;
        }, disconnectIntentTimeoutDuration);
    }

    public async join() {
        stats.increment("teleop_session_join", { deviceId: this.deviceId });
        this.connectTries = 0;
        const connection = await this.connect();
        if (!connection) {
            this.sessionStatus = "error";
            return;
        }
    }

    public async stop() {
        stats.increment("teleop_session_stop", { deviceId: this.deviceId });
        await this.refreshSessionInfo();
        this.hasStopped = true;
        track("Teleop Session intentional disconnect", {
            "Teleop Session ID": this.uuid,
            "Session ID": this.sessionId,
        });

        if (this.updatedDeviceConfiguration) {
            fork(this.updateDeviceConfiguration());
        }

        await this.shutdown();
    }

    public async getDeviceConfiguration(): Promise<
        IDeviceConfiguration | undefined
    > {
        if (this.deviceConfiguration) {
            return this.deviceConfiguration;
        }

        const deviceConfiguration = this.deviceConfigurationStore.get(
            this.deviceId,
        );
        if (deviceConfiguration === undefined) {
            await delay(duration.second * 0.2);
            return this.getDeviceConfiguration();
        }

        if (deviceConfiguration === false) {
            return undefined;
        }

        this.originalDeviceConfiguration = clone(deviceConfiguration);

        return (this.deviceConfiguration = deviceConfiguration);
    }

    public getPingInfo(): IPingInfo | undefined {
        const { rtcClient } = this;
        if (!rtcClient) {
            return;
        }

        if (!this.devicePeer) {
            return;
        }

        const { devicePeer } = this;
        if (!devicePeer || this.getConnectionStatus() !== "connected") {
            return (this.ping = undefined);
        }
        if (rtcClient instanceof RtcClient) {
            return rtcClient.getPingInfo(devicePeer.id);
        }
        return undefined;
    }

    public async getConnectionStatsInfo(): Promise<
        IRtcConnectionStatsInfo | undefined
    > {
        const { rtcClient } = this;
        if (!rtcClient) {
            return;
        }

        if (!this.devicePeer) {
            return;
        }

        const { devicePeer } = this;
        if (!devicePeer || this.getConnectionStatus() !== "connected") {
            return undefined;
        }
        if (rtcClient instanceof RtcClient) {
            return rtcClient.getConnectionStatsInfo(devicePeer.id);
        }
        return undefined;
    }

    public async getPing(): Promise<number | undefined> {
        const { rtcClient } = this;
        if (!rtcClient) {
            return;
        }

        if (!this.devicePeer) {
            return;
        }

        const { devicePeer } = this;
        if (!devicePeer || this.getConnectionStatus() !== "connected") {
            return (this.ping = undefined);
        }

        const ping = rtcClient.getPing(devicePeer.id);

        if (!ping) {
            return undefined;
        }

        this.recentPings.unshift(ping);

        if (this.recentPings.length > 5) {
            this.recentPings = this.recentPings.slice(0, 5);
        }

        const averagePing =
            this.recentPings.reduce((a, b) => a + b, 0) /
            this.recentPings.length;

        if (averagePing > duration.second * 10) {
            await this.restart();
            this.recentPings = [];
        }

        return (this.ping = ping);
    }

    public getLastMessageTimestamp(): Timestamp | undefined {
        const { devicePeer, rtcClient } = this;
        this.lastMessageTimestamp =
            (devicePeer && rtcClient?.getLastMessageTimestamp(devicePeer.id)) ||
            this.lastMessageTimestamp;
        return this.lastMessageTimestamp;
    }

    public getSessionMetricsMessageCounts():
        | IRtcSessionMetricsMessageCounts
        | undefined {
        const { rtcClient } = this;
        if (!rtcClient || !this.isRtcV2(rtcClient)) {
            return;
        }

        if (!this.devicePeer) {
            return;
        }

        const { devicePeer } = this;
        if (!devicePeer || this.getConnectionStatus() !== "connected") {
            return;
        }

        return rtcClient.getSessionMetricsMessageCounts(devicePeer.id);
    }

    public controlRemoteStream(streamControl: IStreamControl) {
        const { rtcClient } = this;
        if (!rtcClient) {
            return;
        }

        if (!this.devicePeer) {
            return;
        }

        if (this.connectionStatus !== "connected") {
            return;
        }

        rtcClient.controlRemoteStream(this.devicePeer.id, streamControl);
    }

    public send(msg: IRtcStreamMessage, config = defaultSendConfig) {
        const { rtcClient } = this;
        if (!rtcClient) {
            return;
        }

        if (!this.devicePeer || this.connectionStatus !== "connected") {
            return;
        }

        rtcClient.send(this.devicePeer.id, msg, config);
    }

    public registerReceiver(
        streamName: string,
        receiver: (msg: IRtcStreamMessage) => void,
    ) {
        logger.info(`Registering ${streamName} with session ${this.uuid}`);
        const id = uuid();
        if (this.registeredReceivers[streamName] === undefined) {
            this.registeredReceivers[streamName] = {};
        }
        this.registeredReceivers[streamName][id] = receiver;
        return this.unregisterReceiver(id, streamName);
    }

    public disableControls() {
        this.onDisableControls?.();
    }

    public async createDefaultTeleopDeviceConfiguration() {
        const id = this.deviceId;
        const { navigate, routeTo } = this.routerStore;

        try {
            const device = await deviceClient.get(
                await this.authStore.token(),
                id,
            );
            let currentConfiguration: IDeviceConfiguration = {
                document: {},
            };
            if (device?.desiredConfigurationVersion) {
                currentConfiguration = await deviceClient.getConfiguration(
                    await this.authStore.token(),
                    id,
                    device?.desiredConfigurationVersion,
                );
            }
            const document = currentConfiguration?.document || {};
            document.teleop = {
                rosStreams: [
                    {
                        topicName: "/cmd_vel",
                        topicType: "geometry_msgs/Twist",
                        mode: "command",
                    },
                ],
                joysticks: [
                    {
                        position: "right",
                        x: {
                            scale: 1,
                            expo: 2,
                            dimension: "angular-z",
                            gamepadAxis: 2,
                        },
                        y: {
                            scale: 1,
                            expo: 2,
                            dimension: "linear-x",
                            gamepadAxis: 3,
                        },
                    },
                ],
            };

            await deviceClient.updateConfiguration(
                await this.authStore.token(),
                id,
                {
                    deviceId: id,
                    document: {
                        ...document,
                    },
                },
            );

            this.appStore.setSnackbar(
                `${this.device.name} default configuration created`,
                {
                    label: "View",
                    onClick: () => navigate(routeTo.device({ id })),
                },
            );
        } catch (error) {
            this.appStore.setSnackbar(`${this.device.name} failed to save`, {
                label: "Device configuration",
                onClick: () => navigate(routeTo.device({ id })),
            });
            return;
        } finally {
            await this.restart();
        }
    }

    public async updateDeviceConfiguration() {
        const { updatedDeviceConfiguration } = this;
        const { navigate, routeTo } = this.routerStore;

        if (!updatedDeviceConfiguration) {
            return;
        }
        const id = this.deviceId;
        const document = updatedDeviceConfiguration?.document;

        if (!document) {
            return;
        }

        try {
            await deviceClient.updateConfiguration(
                await this.authStore.token(),
                id,
                {
                    deviceId: id,
                    document: {
                        ...document,
                    },
                },
            );

            this.appStore.setSnackbar(
                `${this.device.name} configuration updated`,
                {
                    label: "View",
                    onClick: () => navigate(routeTo.device({ id })),
                },
            );
        } catch (error) {
            this.appStore.setSnackbar(`${this.device.name} failed to save`, {
                label: "Device configuration",
                onClick: () => navigate(routeTo.device({ id })),
            });
            return;
        } finally {
            this.updatedDeviceConfiguration = undefined;
            this.originalDeviceConfiguration = undefined;

            await this.getDeviceConfiguration();
        }
    }

    private async connect(): Promise<Uuid | false> {
        const { rtcClient, hasStopped } = this;

        if (!rtcClient || hasStopped) {
            return false;
        }

        await this.getOrganization();
        const { organization } = this;
        if (!organization) {
            return false;
        }

        this.connectTries++;
        if (this.connectTries > 2) {
            this.sessionStatus = "error";
            return false;
        }

        const peer = await this.getDevicePeer();
        if (!peer) {
            return await this.connect();
        }

        const {
            document: { rtcInfo },
        } = defined(await this.getDeviceConfiguration());

        this.sessionStatus = "confirmed";
        this.connectionStatus = "connecting";
        const lockDevice = this.appStore.teleopLockDevice;
        logger.debug("TeleopSession connect: passing lockDevice to rtcClient", {
            lockDevice,
            peerId: peer.id,
        });
        const newSession = await rtcClient.connect(peer.id, rtcInfo, {
            lockDevice,
        });

        if (!newSession) {
            return await this.connect();
        }

        return (this.sessionId = newSession);
    }

    private onConnected = () => {
        const { devicePeer } = this;
        if (!devicePeer || !devicePeer.deviceId) {
            return;
        }

        const { deviceConfiguration } = this;
        if (!deviceConfiguration) {
            return;
        }

        const views = deviceConfiguration.document.teleop?.views;
        if (!views) {
            return;
        }

        const capabilitySet = getRtcCapabilitySet(deviceConfiguration);
        if (!capabilitySet) {
            return;
        }

        this.hasConnected = true;
        this.firstConnect = false;
        this.connectedTimestamp = new Date().getTime();

        track("Teleop Session Connect", {
            "Teleop Session ID": this.uuid,
            "Device ID": this.deviceId,
        });

        // Grabs disconnection time once connection established
        this.updateOnConnectedSessionMetrics(this.connectedTimestamp);

        for (const _ of capabilitySet.streaming?.streams || []) {
            const { streamName, streamType } = _.stream;
            const isVideo = rtcImageStreamTypes.includes(streamType);
            const inView = views.some(view => view.streamName === streamName);
            if (!isVideo || inView) {
                this.controlRemoteStream({
                    streamName,
                    enable: true,
                    pipeline: rtcPipelineType,
                });
            }
        }
    };

    public async getDevicePeer(): Promise<IRtcPeer | undefined> {
        const { rtcClient } = this;
        if (!rtcClient) {
            return;
        }

        const deviceDetails = await this.getDeviceDetails();
        if (!deviceDetails) {
            return undefined;
        }

        this.devicePeer = (await rtcClient.getPeers()).find(
            ({ deviceId, capabilitySet }: IRtcPeer) =>
                deviceId === deviceDetails.id &&
                capabilitySet.portForwarding === undefined, // Note: backwards compatible with v1 streaming peers and v2 peers
        );
        return this.devicePeer || undefined;
    }

    private async getOrganization(): Promise<IOrganization | undefined> {
        if (this.organization) {
            return this.organization;
        }

        const organizationId = this.authStore.organizationId;
        if (!organizationId) {
            return;
        }

        const organization = this.organizationStore.get(organizationId);
        if (organization === undefined) {
            await delay(duration.second * 0.2);
            return this.getOrganization();
        }

        if (organization === false) {
            return;
        }

        return (this.organization = organization);
    }
    public getConnectionStatus(): RtcConnectionStatus {
        const { rtcClient } = this;
        if (!rtcClient) {
            return "disconnected";
        }

        const { connectionStatus } = this;
        this.connectionStatus = !this.devicePeer
            ? "disconnected"
            : rtcClient.getConnectionStatus(this.devicePeer.id);

        if (
            connectionStatus !== "connected" &&
            this.connectionStatus === "connected"
        ) {
            this.onConnected();
        }

        if (
            connectionStatus === "connected" &&
            this.connectionStatus !== "connected"
        ) {
            track("Teleop Session Disconnect", {
                "Teleop Session ID": this.uuid,
                "Device ID": this.deviceId,
            });
        }

        return this.connectionStatus;
    }

    private async getIsOwner(): Promise<boolean> {
        const { rtcClient, ownerUserPeer } = this;

        if (!rtcClient || !ownerUserPeer) {
            return false;
        }

        const userPeer = await rtcClient.getLocalPeer();
        return (this.owner = userPeer.id === ownerUserPeer.id);
    }

    private async shutdown() {
        const { rtcClient, devicePeer, updateInterval } = this;
        teleopDebug("Shutting down teleop session", {
            peerId: (devicePeer || undefined)?.id,
            deviceId: (devicePeer || undefined)?.deviceId,
        });

        await rtcClient?.shutdown();
        await updateInterval?.stop();
    }

    private async update() {
        const { rtcClient } = this;
        if (!rtcClient) {
            return;
        }

        // Sometimes the rtcClient and/or updateInterval are not loaded yet when the
        // "stop" method of this class is first called,
        // so we need to make sure the update loop shuts itself down.
        if (this.hasStopped) {
            teleopDebug(
                "TeleopSession update: Session stopped. Shutting down updates.",
            );
            await this.shutdown();
            return;
        }

        await this.refreshSessionInfo();

        if (this.devicePeer) {
            if (this.isRtcV2(rtcClient)) {
                const connectionStatsInfo =
                    await rtcClient.getConnectionStatsInfo(this.devicePeer.id);
                if (connectionStatsInfo) {
                    const candidateTypesLog = `local: ${connectionStatsInfo.localCandidate.candidateType}, remote: ${connectionStatsInfo.remoteCandidate.candidateType}`;
                    if (this.candidateTypesLog !== candidateTypesLog) {
                        // Only log when candidate types change
                        this.candidateTypesLog = candidateTypesLog;
                        logger.info(
                            `Candidate types: ${this.candidateTypesLog}`,
                        );
                    }
                    this.iceMode =
                        iceModeFromRtcConnectionStatsInfo(connectionStatsInfo);
                }
            } else {
                this.iceMode = rtcClient.getIceMode(this.devicePeer.id);
            }
        }

        // Update the local teleop session state
        const { connectionStatus, sessionStatus } = this;

        this.onUpdateSessionMetrics(connectionStatus, sessionStatus);
        this.updateCommandSessionMetrics();

        const connectingFailure =
            sessionStatus === "confirmed" &&
            connectionStatus === "disconnected";

        const midSessionDisconnect =
            sessionStatus === "started" && connectionStatus === "disconnected";

        const sessionAvailable =
            sessionStatus === "noSession" && this.devicePeer !== undefined;

        const isEstablishing =
            (sessionStatus === "hasSession" || sessionStatus === "noSession") &&
            connectionStatus === "connecting";

        const hasConnection = connectionStatus === "connected";

        if (
            connectingFailure ||
            midSessionDisconnect ||
            sessionStatus === "error"
        ) {
            teleopDebug(
                "TeleopSession update: Restart called due to error...",
                {
                    connectionStatus,
                    sessionAvailable,
                    isEstablishing,
                    hasConnection,
                    connectingFailure,
                    midSessionDisconnect,
                    sessionStatus,
                },
            );
            this.sessionStatus = "disconnected";
            const reason = connectingFailure
                ? "connecting failure"
                : midSessionDisconnect
                  ? "mid session disconnect"
                  : "session error";
            teleopDebug(
                `TeleopSession update: Restart called due to perceived ${reason}`,
                {
                    sessionStatus: this.sessionStatus,
                    connectionStatus: this.connectionStatus,
                    sessionAvailable,
                    isEstablishing,
                    hasConnection,
                },
            );

            if (midSessionDisconnect) {
                if (!this.disconnectIntent) {
                    track("Teleop Session unintentional disconnect", {
                        "Teleop Session ID": this.uuid,
                        "Session ID": this.sessionId,
                    });
                }
            }

            await this.restart();
            return;
        }
        if (sessionAvailable) {
            this.sessionStatus = "hasSession";
        }
        if (isEstablishing) {
            this.sessionStatus = "confirmed";
        }
        if (hasConnection) {
            this.sessionStatus = "started";
        }

        // Restart if the device peer becomes unresponsive.

        const lastActivityTimestamp =
            this.getLastMessageTimestamp() ?? this.connectedTimestamp;
        if (
            hasConnection &&
            lastActivityTimestamp &&
            new Date().getTime() >
                lastActivityTimestamp + unresponsiveRestartThreshold
        ) {
            teleopDebug("Restart called due to unresponsive device peer.", {
                sessionStatus,
                connectionStatus,
                sessionAvailable,
                isEstablishing,
                hasConnection,
            });

            await this.restart();
            return;
        }
    }

    private recentDeviceActivity(): boolean {
        const lastActivityTimestamp =
            this.getLastMessageTimestamp() ?? this.connectedTimestamp;

        return (
            lastActivityTimestamp !== undefined &&
            Date.now() < lastActivityTimestamp + unresponsiveRestartThreshold
        );
    }

    private async updateSessionKnowledge() {
        const deviceDetails = await this.getDeviceDetails();
        if (!deviceDetails) {
            return;
        }

        // Fetch RTC signaling sessions and peers if useSignalingV2 is false
        if (!this.useSignalingV2) {
            await this.rtcSignalingStore.fetchSessions();
            await this.rtcSignalingStore.fetchPeers();

            const allPeers = this.rtcSignalingStore.peers;
            const allSessions = this.rtcSignalingStore.sessions;
            logger.info("RTC Signaling Store", {
                peers: allPeers,
                sessions: allSessions,
            });
        }

        // Fetch peers and sessions from signaling store
        // before we determine the owner.
        // signalingStore is used for v2 signaling
        const fetchedSessions = await this.signalingStore.fetchSessions();
        const fetchedPeers = await this.signalingStore.fetchPeers();

        teleopDebug(
            "TeleopSession updateSessionKnowledge: Fetched sessions and peers",
            {
                fetchedSessions: fetchedSessions.length,
                fetchedPeers: fetchedPeers.length,
            },
        );

        // Determine the owner for v1
        let v1Owner;
        if (!this.useSignalingV2) {
            v1Owner =
                this.rtcSignalingStore.ownerPeerForDeviceTeleop(deviceDetails);
        }

        // Determine the owner for v2
        let v2Owner;

        const v2OwnerPeerList =
            await this.signalingStore.userPeersInTeleopSessionWithDevice(
                deviceDetails,
            );

        if (v2OwnerPeerList === undefined || v2OwnerPeerList.length === 0) {
            this.ownerUserPeer = null;
            await this.getIsOwner();
            return;
        }
        const v2OwnerPeer = v2OwnerPeerList.find(_ => _);
        if (v2OwnerPeer) {
            v2Owner = peerToRtcPeer(v2OwnerPeer);
        }

        this.ownerUserPeer = v1Owner || v2Owner;
        const v2OwnerPeerListArrays = v2OwnerPeerList.map(peer =>
            peer.toArray(),
        );
        const isOwner = await this.getIsOwner();
        teleopDebug("TeleopSession updateSessionKnowledge: Owner Peers", {
            v2OwnerPeerList: v2OwnerPeerListArrays || null,
            v2OwnerID: v2Owner?.userId || null,
            ownerUserPeerID: this.ownerUserPeer?.userId || null,
            isOwner,
        });
    }

    private async checkConnection() {
        const { rtcClient } = this;
        if (!rtcClient) {
            return;
        }

        const { devicePeer } = this;

        if (!this.hasConnected) {
            return;
        }

        if (!devicePeer) {
            return;
        }

        this.connectionStatus = rtcClient.getConnectionStatus(devicePeer.id);
    }

    private async getDeviceDetails(): Promise<IDeviceDetails | undefined> {
        if (this.deviceDetails) {
            return this.deviceDetails;
        }

        const deviceDetails = this.deviceDetailsStore.get(this.deviceId);
        if (deviceDetails === undefined) {
            await delay(duration.second * 0.2); // [AN] ?! Is this a hack. Why a delay?
            return this.getDeviceDetails();
        }

        if (deviceDetails === false) {
            return undefined;
        }

        return (this.deviceDetails = deviceDetails);
    }

    private receive(msg: IRtcStreamMessage) {
        const { streamName } = msg.header.stream;
        if (this.receivedStreams[streamName] === undefined) {
            logger.info(`Received ${streamName} on session ${this.uuid}`);
            this.receivedStreams[streamName] = true;
        }
        const receivers = this.registeredReceivers[streamName];
        if (receivers === undefined) {
            return;
        }
        Object.keys(receivers).forEach(key => receivers[key](msg));
    }

    private onStreamsInfoUpdate(timestamp: Timestamp) {
        const counts = this.getSessionMetricsMessageCounts();
        if (!counts) {
            return;
        }

        if (!this.lastStreamsInfoUpdate) {
            this.lastStreamsInfoUpdate = timestamp;
            return;
        }

        const { localReceived, remoteSent } = counts;
        const prevLocalReceived = this.localReceived;
        const prevRemoteSent = this.remoteSent;

        const timeSinceLastUpdateSeconds =
            (timestamp - this.lastStreamsInfoUpdate) / duration.second;
        this.lastStreamsInfoUpdate = timestamp;

        const uniqueStreamIds = new Set([
            ...localReceived.keys(),
            ...remoteSent.keys(),
        ]);

        // Degrade quality of any video streams which meet the degradation criteria
        for (const streamId of uniqueStreamIds) {
            const { streamName, streamType } = decodeStreamId(streamId);
            if (streamType !== rtcH264VideoFrameStreamType) {
                continue;
            }

            const prevRemoteSentCount = prevRemoteSent.get(streamId) || 0;
            const prevLocalReceivedCount = prevLocalReceived.get(streamId) || 0;
            const currentRemoteSentCount = remoteSent.get(streamId) || 0;
            const currentLocalReceivedCount = localReceived.get(streamId) || 0;

            // update the teleop session's store of the
            // previous window's message counts with the new message counts
            this.remoteSent.set(streamId, currentRemoteSentCount);
            this.localReceived.set(streamId, currentRemoteSentCount);

            const remoteSentDiff = currentRemoteSentCount - prevRemoteSentCount;
            const localReceivedDiff =
                currentLocalReceivedCount - prevLocalReceivedCount;

            // The fps collection window duration is streamsInfoUpdateTimeoutMs on RtcConnection
            const fps = remoteSentDiff / timeSinceLastUpdateSeconds;
            const dropRatio = localReceivedDiff / remoteSentDiff;
            if (this.reportFramesDroppedMap[streamName] === undefined) {
                this.reportFramesDroppedMap[streamName] = throttle(
                    this.reportFramesDroppedFn(streamName),
                    10 * duration.second,
                );
            }
            this.reportFramesDroppedMap[streamName](
                dropRatio,
                remoteSentDiff,
                localReceivedDiff,
                fps,
                currentLocalReceivedCount,
                prevLocalReceivedCount,
            );
            if (
                fps > 10 && // 1. Remote is sending at least the minimum framerate
                dropRatio < 0.8 // 2. Less than 80% of messages made it
            ) {
                this.degradeVideoQuality(streamName);
            }
        }
    }

    private reportFramesDroppedFn =
        (streamName: string) =>
        (
            dropRatio: number,
            remoteSentDiff: number,
            localReceivedDiff: number,
            fps: number,
            currentLocalReceivedCount: number,
            prevLocalReceivedCount: number,
        ) => {
            track("Video frames sent by the device are not being received", {
                "Session ID": this.sessionId,
                "Teleop Session ID": this.uuid,
                Stream: streamName,
                "Drop Ratio": dropRatio,
                "Remote Send Diff": remoteSentDiff,
                "Local Send Diff": localReceivedDiff,
                FPS: fps,
                "Current Local Received Count": currentLocalReceivedCount,
                "Previous Local Received Count": prevLocalReceivedCount,
            });
            logger.info(
                "Video frames sent by the device are not being received",
                {
                    streamName,
                    remoteSentDiff,
                    localReceivedDiff,
                    fps,
                    currentLocalReceivedCount,
                    prevLocalReceivedCount,
                },
            );
        };

    private onRestartSessionMetrics() {
        if (this.teleopRestartTimestamp === undefined) {
            this.teleopInitialRestartTimestamp = Date.now();
        }

        this.teleopRestartTimestamp = Date.now();
        stats.increment("teleop_restart_called", { deviceId: this.deviceId });
    }

    private onUpdateSessionMetrics(
        connectionStatus: RtcConnectionStatus,
        sessionStatus: SessionLifecycle,
    ) {
        if (this.startedTimestamp) {
            const disconnectionPercentage =
                100 *
                (this.disconnectAggregate /
                    (Date.now() - this.startedTimestamp));
            stats.aggregate(
                "teleop_disconnection_percentage",
                disconnectionPercentage,
                { deviceId: this.deviceId },
            );
        }

        if (
            sessionStatus !== "started" &&
            connectionStatus !== "connected" &&
            !this.firstConnect
        ) {
            if (this.disconnectTimestamp === undefined) {
                stats.increment("teleop_disconnection_count", {
                    deviceId: this.deviceId,
                });
                this.disconnectTimestamp = Date.now();
            } else {
                stats.aggregate(
                    "teleop_rolling_disconnect_time",
                    Date.now() - this.disconnectTimestamp,
                    { deviceId: this.deviceId },
                );
            }
        }

        if (sessionStatus === "started" && connectionStatus === "connected") {
            stats.increment("teleop_active_device", {
                deviceid: this.deviceId,
            });
        }
    }

    private updateCommandSessionMetrics() {
        // Currently only supports joysticks or buttons
        const teleopInputNames = new Set(["/cmd_vel", "Buttons"]);
        const counts = this.getSessionMetricsMessageCounts();

        if (counts === undefined) {
            return;
        }
        const { localSent, remoteReceived } = counts;

        // TODO: Support all teleop command stream names
        for (const streamId of [...localSent.keys()].filter(_ =>
            teleopInputNames.has(decodeStreamId(_).streamName),
        )) {
            const commandReceived = remoteReceived.get(streamId);
            const commandSent = localSent.get(streamId);

            const lastSent = this.commandStreamIdSentMap.get(streamId);

            if (
                commandReceived !== undefined &&
                commandSent !== undefined &&
                lastSent !== undefined &&
                lastSent.count === commandSent &&
                // only confirm commands sent after 1 second of stream inactivity
                differenceInMilliseconds(new Date(), lastSent.timestamp) >
                    duration.second
            ) {
                stats.aggregate(
                    "teleop_command_confirmed_percentage",
                    100 * (commandReceived / commandSent),
                    { deviceId: this.deviceId },
                );
            }

            if (commandSent !== undefined) {
                this.commandStreamIdSentMap.set(streamId, {
                    count: commandSent,
                    timestamp:
                        lastSent !== undefined && lastSent.count === commandSent
                            ? lastSent.timestamp
                            : Date.now(),
                });
            }
        }
    }

    private updateOnConnectedSessionMetrics(connectedTimestamp: Timestamp) {
        if (this.disconnectTimestamp) {
            stats.aggregate(
                "teleop_disconnection_to_connection_time",
                connectedTimestamp - this.disconnectTimestamp,
                { deviceId: this.deviceId },
            );
            this.disconnectAggregate +=
                connectedTimestamp - this.disconnectTimestamp;

            this.disconnectTimestamp = undefined;
        }

        stats.aggregate(
            "teleop_total_disconnection_time",
            this.disconnectAggregate,
            { deviceId: this.deviceId },
        );
        stats.aggregate(
            "teleop_connection_time",
            connectedTimestamp -
                defined(this.teleopRestartTimestamp ?? this.startedTimestamp),
            {
                restart: `${this.teleopRestartTimestamp !== undefined}`,
                deviceId: this.deviceId,
            },
        );

        if (this.teleopRestartTimestamp !== undefined) {
            stats.increment("teleop_restart_complete", {
                deviceId: this.deviceId,
            });
            stats.aggregate(
                "teleop_initial_call_restart_completed_time",
                connectedTimestamp -
                    defined(this.teleopInitialRestartTimestamp),
                {
                    firstRestart: `${
                        this.teleopInitialRestartTimestamp !== undefined
                    }`,
                    deviceId: this.deviceId,
                },
            );
            stats.aggregate(
                "teleop_latest_restart_connection_time",
                connectedTimestamp - this.teleopRestartTimestamp,
                { deviceId: this.deviceId },
            );

            this.teleopRestartTimestamp = undefined;
            this.teleopInitialRestartTimestamp = undefined;
        }
    }

    private unregisterReceiver(id: string, streamName: string) {
        return () => {
            logger.info(
                `Unregistering ${streamName} with session ${this.uuid}`,
            );
            return delete this.registeredReceivers[streamName][id];
        };
    }

    private async refreshSessionInfo() {
        if (!this.recentDeviceActivity()) {
            await this.getDevicePeer();
        }
        await this.getDeviceConfiguration();
        await this.getOrganization();
        await this.updateSessionKnowledge();
        await this.checkConnection();
    }
}

decorate(TeleopSession, {
    sessionStatus: observable,
    connectionStatus: observable,
    ping: observable,
    ownerUserPeer: observable,
    owner: observable,
    devicePeer: observable,
    iceMode: observable,
    hasStopped: observable,
});
